/**
 * A minimal .xlsx writer, the mirror of `xlsx.ts`. ZIP STORE (no compression) keeps it synchronous and testable under node.
 * ⛔ Not a general writer: inline strings, numbers, booleans, dates, a fixed style palette, merges, frozen panes and column widths only.
 * The fixed palette is deliberate: every book looks the same and pages ask for `'head'` or `'total'`.
 */


/** Style palette: callers name a role, never a colour. `*Alt` are the zebra twins. */
export type StyleName =
  | 'title'
  | 'metaKey'
  | 'metaVal'
  | 'head'
  | 'sub'
  | 'cell'
  | 'cellAlt'
  | 'date'
  | 'dateAlt'
  | 'num'
  | 'numAlt'
  | 'total'
  | 'totalNum'
  | 'ok'
  | 'due'
  | 'bad'
  | 'tintA'
  | 'tintB'
  | 'money'
  | 'moneyAlt'
  | 'totalMoney'

export interface StyledCell {
  v: string | number | boolean | null
  s?: StyleName
  /** Force the storage type for a value that looks numeric but is not a quantity (a hole id like `026`). */
  t?: 'text'
}

export type Cell = string | number | boolean | null | StyledCell

export interface WriteSheet {
  name: string
  rows: Cell[][]
  cols?: number[]
  merges?: string[]
  /** Top-left of the scrolling pane, e.g. `"C4"`. */
  freeze?: string
}


/** Escape XML text. ⚠️ Control characters are stripped: XML 1.0 cannot represent them and one makes the whole workbook unopenable. */
export function escapeXml(text: string): string {
  let out = ''
  for (const ch of text) {
    const code = ch.codePointAt(0) ?? 0
    if (code < 0x20 && ch !== '\t' && ch !== '\n' && ch !== '\r') continue
    if (ch === '&') out += '&amp;'
    else if (ch === '<') out += '&lt;'
    else if (ch === '>') out += '&gt;'
    else if (ch === '"') out += '&quot;'
    else out += ch
  }
  return out
}

export function columnName(index: number): string {
  let name = ''
  let n = index
  while (n >= 0) {
    name = String.fromCharCode(65 + (n % 26)) + name
    n = Math.floor(n / 26) - 1
  }
  return name
}

/** Excel serial day for an ISO date. The epoch is 1899-12-30 to absorb Excel's phantom 1900-02-29. */
export function isoToSerial(iso: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  if (!m) return null
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  return Math.round((ms - Date.UTC(1899, 11, 30)) / 86400000)
}

/** Repair rather than throw on an unusable tab name (forbidden characters, 31-char cap, edge apostrophes). */
function tabName(name: string): string {
  const cleaned = name.replace(/[[\]:*?/\\]/g, ' ').slice(0, 31)
  return cleaned.replace(/^'+|'+$/g, '').trim() || 'Sheet'
}


/** Palette as indices into `cellXfs`; the order IS that list's order, so edit them together. */
const STYLE_ORDER: StyleName[] = [
  'title',
  'metaKey',
  'metaVal',
  'head',
  'sub',
  'cell',
  'cellAlt',
  'date',
  'dateAlt',
  'num',
  'numAlt',
  'total',
  'totalNum',
  'ok',
  'due',
  'bad',
  'tintA',
  'tintB',
  // Append only, never insert: an index is already written into every book emitted.
  'money',
  'moneyAlt',
  'totalMoney',
]

const STYLE_INDEX: Record<StyleName, number> = Object.fromEntries(
  STYLE_ORDER.map((name, i) => [name, i + 1]),
) as Record<StyleName, number>

const DATE_STYLES = new Set<StyleName>(['date', 'dateAlt'])

function stylesXml(): string {
  const fonts = [
    '<font><sz val="11"/><name val="Calibri"/></font>',
    '<font><b/><sz val="11"/><name val="Calibri"/></font>',
    '<font><b/><sz val="14"/><name val="Calibri"/></font>',
    '<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>',
  ]
  // fills: 0 none and 1 gray125 are required by the format in that order.
  const solid = (rgb: string) =>
    `<fill><patternFill patternType="solid"><fgColor rgb="FF${rgb}"/><bgColor indexed="64"/></patternFill></fill>`
  const fills = [
    '<fill><patternFill patternType="none"/></fill>',
    '<fill><patternFill patternType="gray125"/></fill>',
    solid('334155'), // 2 head
    solid('F1F5F9'), // 3 zebra / sub
    solid('DCFCE7'), // 4 ok
    solid('FEF3C7'), // 5 due
    solid('FEE2E2'), // 6 bad
    solid('EFF6FF'), // 7 tintA
    solid('F5F3FF'), // 8 tintB
    solid('E2E8F0'), // 9 total
  ]
  const thin =
    '<border><left style="thin"><color rgb="FFCBD5E1"/></left><right style="thin"><color rgb="FFCBD5E1"/></right>' +
    '<top style="thin"><color rgb="FFCBD5E1"/></top><bottom style="thin"><color rgb="FFCBD5E1"/></bottom></border>'
  const borders = ['<border/>', thin]

  const numFmts =
    '<numFmts count="2">' +
    '<numFmt numFmtId="164" formatCode="yyyy\\-mm\\-dd"/>' +
    '<numFmt numFmtId="165" formatCode="#,##0.##"/>' +
    '</numFmts>'

  const xf = (
    font: number,
    fill: number,
    border: number,
    numFmt = 0,
    align = '',
  ) =>
    `<xf numFmtId="${numFmt}" fontId="${font}" fillId="${fill}" borderId="${border}" xfId="0"` +
    ` applyFont="1" applyFill="1" applyBorder="1"${numFmt ? ' applyNumberFormat="1"' : ''}` +
    (align ? ` applyAlignment="1">${align}</xf>` : '/>')

  const centre = '<alignment horizontal="center" vertical="center" wrapText="1"/>'
  const left = '<alignment horizontal="left" vertical="center"/>'

  // MUST stay in STYLE_ORDER's order -- see the note there.
  const cellXfs = [
    '<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>', // default
    xf(2, 0, 0, 0, left), // title
    xf(1, 0, 0, 0, left), // metaKey
    xf(0, 0, 0, 0, left), // metaVal
    xf(3, 2, 1, 0, centre), // head
    xf(1, 3, 1, 0, centre), // sub
    xf(0, 0, 1), // cell
    xf(0, 3, 1), // cellAlt
    xf(0, 0, 1, 164), // date
    xf(0, 3, 1, 164), // dateAlt
    xf(0, 0, 1, 165), // num
    xf(0, 3, 1, 165), // numAlt
    xf(1, 9, 1), // total
    xf(1, 9, 1, 165), // totalNum
    xf(0, 4, 1), // ok
    xf(0, 5, 1), // due
    xf(0, 6, 1), // bad
    xf(0, 7, 1), // tintA
    xf(0, 8, 1), // tintB
    // Built-in numFmtId 4 (`#,##0.00`): money always shows its cents.
    xf(0, 0, 1, 4), // money
    xf(0, 3, 1, 4), // moneyAlt
    xf(1, 9, 1, 4), // totalMoney
  ]

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    numFmts +
    `<fonts count="${fonts.length}">${fonts.join('')}</fonts>` +
    `<fills count="${fills.length}">${fills.join('')}</fills>` +
    `<borders count="${borders.length}">${borders.join('')}</borders>` +
    '<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>' +
    `<cellXfs count="${cellXfs.length}">${cellXfs.join('')}</cellXfs>` +
    '<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>' +
    '</styleSheet>'
  )
}


function cellXml(ref: string, cell: Cell): string {
  const styled: StyledCell =
    cell !== null && typeof cell === 'object' ? cell : { v: cell }
  const { v, s, t } = styled
  const si = s ? STYLE_INDEX[s] : 0
  const attr = si ? ` s="${si}"` : ''

  if (v === null || v === '') return si ? `<c r="${ref}"${attr}/>` : ''

  if (typeof v === 'boolean') {
    return `<c r="${ref}"${attr} t="b"><v>${v ? 1 : 0}</v></c>`
  }

  // A string in a date column is an ISO date that must become a serial.
  if (s && DATE_STYLES.has(s) && typeof v === 'string') {
    const serial = isoToSerial(v)
    if (serial !== null) return `<c r="${ref}"${attr}><v>${serial}</v></c>`
  }

  if (typeof v === 'number' && Number.isFinite(v)) {
    return `<c r="${ref}"${attr}><v>${v}</v></c>`
  }

  const text = String(v)
  // Non-finite numbers and `t="text"` fall through to an inline string; XLSX cannot represent NaN/Infinity.
  void t
  return `<c r="${ref}"${attr} t="inlineStr"><is><t xml:space="preserve">${escapeXml(text)}</t></is></c>`
}

function sheetXml(sheet: WriteSheet): string {
  const rows = sheet.rows
    .map((cells, r) => {
      const body = cells
        .map((cell, c) => cellXml(`${columnName(c)}${r + 1}`, cell))
        .join('')
      return body ? `<row r="${r + 1}">${body}</row>` : ''
    })
    .join('')

  const cols = sheet.cols?.length
    ? '<cols>' +
      sheet.cols
        .map((w, i) =>
          w ? `<col min="${i + 1}" max="${i + 1}" width="${w}" customWidth="1"/>` : '',
        )
        .join('') +
      '</cols>'
    : ''

  // The split point is needed twice: as a cell ref and as x/y counts.
  let pane = ''
  if (sheet.freeze) {
    const m = /^([A-Z]+)(\d+)$/.exec(sheet.freeze)
    if (m) {
      const x = columnIndexOf(m[1])
      const y = Number(m[2]) - 1
      pane =
        `<pane xSplit="${x}" ySplit="${y}" topLeftCell="${sheet.freeze}"` +
        ' activePane="bottomRight" state="frozen"/>'
    }
  }

  const merges = sheet.merges?.length
    ? `<mergeCells count="${sheet.merges.length}">` +
      sheet.merges.map((r) => `<mergeCell ref="${r}"/>`).join('') +
      '</mergeCells>'
    : ''

  return (
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">' +
    `<sheetViews><sheetView workbookViewId="0">${pane}</sheetView></sheetViews>` +
    '<sheetFormatPr defaultRowHeight="15"/>' +
    cols +
    `<sheetData>${rows}</sheetData>` +
    merges +
    '</worksheet>'
  )
}

function columnIndexOf(letters: string): number {
  let n = 0
  for (const ch of letters) n = n * 26 + (ch.charCodeAt(0) - 64)
  return n - 1
}


const CRC_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    table[i] = c >>> 0
  }
  return table
})()

export function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8)
  return (c ^ 0xffffffff) >>> 0
}

interface Part {
  name: string
  bytes: Uint8Array
}

function zip(parts: Part[]): Uint8Array {
  const encoder = new TextEncoder()
  const locals: Uint8Array[] = []
  const centrals: Uint8Array[] = []
  let offset = 0

  for (const part of parts) {
    const nameBytes = encoder.encode(part.name)
    const crc = crc32(part.bytes)
    const size = part.bytes.length

    const local = new Uint8Array(30 + nameBytes.length)
    const lv = new DataView(local.buffer)
    lv.setUint32(0, 0x04034b50, true)
    lv.setUint16(4, 20, true) // version needed
    lv.setUint16(6, 0x0800, true) // UTF-8 names
    lv.setUint16(8, 0, true) // method: store
    lv.setUint16(10, 0, true) // time
    lv.setUint16(12, 0x21, true) // date: 1980-01-01, so books are reproducible
    lv.setUint32(14, crc, true)
    lv.setUint32(18, size, true)
    lv.setUint32(22, size, true)
    lv.setUint16(26, nameBytes.length, true)
    local.set(nameBytes, 30)
    locals.push(local, part.bytes)

    const central = new Uint8Array(46 + nameBytes.length)
    const cv = new DataView(central.buffer)
    cv.setUint32(0, 0x02014b50, true)
    cv.setUint16(4, 20, true) // version made by
    cv.setUint16(6, 20, true)
    cv.setUint16(8, 0x0800, true)
    cv.setUint16(10, 0, true)
    cv.setUint16(12, 0, true)
    cv.setUint16(14, 0x21, true)
    cv.setUint32(16, crc, true)
    cv.setUint32(20, size, true)
    cv.setUint32(24, size, true)
    cv.setUint16(28, nameBytes.length, true)
    cv.setUint32(42, offset, true)
    central.set(nameBytes, 46)
    centrals.push(central)

    offset += local.length + size
  }

  const centralSize = centrals.reduce((n, c) => n + c.length, 0)
  const eocd = new Uint8Array(22)
  const ev = new DataView(eocd.buffer)
  ev.setUint32(0, 0x06054b50, true)
  ev.setUint16(8, parts.length, true)
  ev.setUint16(10, parts.length, true)
  ev.setUint32(12, centralSize, true)
  ev.setUint32(16, offset, true)

  const chunks = [...locals, ...centrals, eocd]
  const total = chunks.reduce((n, c) => n + c.length, 0)
  const out = new Uint8Array(total)
  let at = 0
  for (const chunk of chunks) {
    out.set(chunk, at)
    at += chunk.length
  }
  return out
}


/** Build a workbook; returns bytes, not a `Blob`, so it stays testable under node. */
export function writeWorkbook(sheets: WriteSheet[]): Uint8Array {
  if (!sheets.length) throw new Error('a workbook needs at least one sheet')
  const encoder = new TextEncoder()
  const text = (name: string, xml: string): Part => ({
    name,
    bytes: encoder.encode(xml),
  })

  // Uniqueness is enforced after the `tabName` repair, which can collide two long names.
  const used = new Set<string>()
  const names = sheets.map((sheet) => {
    let name = tabName(sheet.name)
    let n = 2
    while (used.has(name.toLowerCase())) {
      const suffix = ` (${n++})`
      name = tabName(name.slice(0, 31 - suffix.length) + suffix)
    }
    used.add(name.toLowerCase())
    return name
  })

  const parts: Part[] = [
    text(
      '[Content_Types].xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">' +
        '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>' +
        '<Default Extension="xml" ContentType="application/xml"/>' +
        '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>' +
        '<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>' +
        sheets
          .map(
            (_, i) =>
              `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`,
          )
          .join('') +
        '</Types>',
    ),
    text(
      '_rels/.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>' +
        '</Relationships>',
    ),
    text(
      'xl/workbook.xml',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"' +
        ' xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships"><sheets>' +
        names
          .map(
            (name, i) =>
              `<sheet name="${escapeXml(name)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`,
          )
          .join('') +
        '</sheets></workbook>',
    ),
    text(
      'xl/_rels/workbook.xml.rels',
      '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>' +
        '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">' +
        sheets
          .map(
            (_, i) =>
              `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`,
          )
          .join('') +
        `<Relationship Id="rId${sheets.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>` +
        '</Relationships>',
    ),
    text('xl/styles.xml', stylesXml()),
    ...sheets.map((sheet, i) =>
      text(`xl/worksheets/sheet${i + 1}.xml`, sheetXml(sheet)),
    ),
  ]

  return zip(parts)
}
